import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetAcquisitionService from '../../services/assetAcquisitionService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { toDateInputValue } from './assetUtils'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  acquisitionCode: '',
  assetName: '',
  assetTag: '',
  serialNumber: '',
  assetType: '',
  vendor: '',
  location: '',
  purchaseDate: '',
  purchaseCost: 0,
  currency: 'INR',
  warrantyExpiry: '',
  status: 'Draft',
  notes: ''
}

const AssetAcquisitionForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const acquisitionData = location.state?.acquisitionData
  const recordId = searchParams.get('id') || acquisitionData?._id

  const [formData, setFormData] = useState(emptyForm)
  const [assetTypes, setAssetTypes] = useState([])
  const [vendors, setVendors] = useState([])
  const [locations, setLocations] = useState([])
  const [statusOptions, setStatusOptions] = useState(optionsFromDropdown(null, 'acquisitionStatus', ASSET_DROPDOWN_FALLBACKS.acquisitionStatus))
  const [currencyOptions, setCurrencyOptions] = useState(optionsFromDropdown(null, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next acquisition code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetAcquisitionService.getNextAcquisitionCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, acquisitionCode: response.data.acquisitionCode || 'AACQ0001' }))
          }
        } catch (error) {
          console.error('Error fetching acquisition code:', error)
          setFormData(prev => ({ ...prev, acquisitionCode: 'AACQ0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  useEffect(() => {
    loadDropdowns()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadAcquisitionData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadDropdowns = async () => {
    try {
      const response = await assetDropdownService.getOptions()
      if (response.success && response.data) {
        setAssetTypes(response.data.assetTypes || [])
        setVendors(response.data.vendors || [])
        setLocations(response.data.locations || [])
        setStatusOptions(optionsFromDropdown(response.data.dropdowns, 'acquisitionStatus', ASSET_DROPDOWN_FALLBACKS.acquisitionStatus))
        setCurrencyOptions(optionsFromDropdown(response.data.dropdowns, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
      } else {
        toast.warn('Could not load master dropdowns. Add Asset Types, Vendors and Locations under Assets → Masters.')
      }
    } catch (error) {
      console.error('Error loading acquisition dropdown data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load dropdown options from masters')
      }
    }
  }

  const loadAcquisitionData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetAcquisitionService.getAcquisitionById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          acquisitionCode: record.acquisitionCode || '',
          assetName: record.assetName || '',
          assetTag: record.assetTag || '',
          serialNumber: record.serialNumber || '',
          assetType: record.assetType?._id || record.assetType || '',
          vendor: record.vendor?._id || record.vendor || '',
          location: record.location?._id || record.location || '',
          purchaseDate: toDateInputValue(record.purchaseDate),
          purchaseCost: record.purchaseCost || 0,
          currency: record.currency || 'INR',
          warrantyExpiry: toDateInputValue(record.warrantyExpiry),
          status: record.status || 'Draft',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading acquisition data:', error)
      toast.error('Failed to load acquisition data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleSave = async () => {
    try {
      if (!formData.assetName) {
        toast.error('Please fill in Asset Name')
        return
      }

      setIsLoading(true)

      const submitData = {
        ...formData,
        assetType: formData.assetType || null,
        vendor: formData.vendor || null,
        location: formData.location || null,
        purchaseDate: formData.purchaseDate || null,
        warrantyExpiry: formData.warrantyExpiry || null
      }

      if (isEditMode && recordId) {
        const response = await assetAcquisitionService.updateAcquisition(recordId, submitData)
        if (response.success) {
          toast.success('Acquisition updated successfully!')
          navigate('/assets/acquisitions')
        } else {
          toast.error(response.message || 'Failed to update acquisition')
        }
      } else {
        const response = await assetAcquisitionService.createAcquisition(submitData)
        if (response.success) {
          toast.success('Acquisition created successfully!')
          navigate('/assets/acquisitions')
        } else {
          toast.error(response.message || 'Failed to create acquisition')
        }
      }
    } catch (error) {
      console.error('Error saving acquisition:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save acquisition')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadAcquisitionData(recordId)
    } else {
      setFormData({ ...emptyForm, acquisitionCode: formData.acquisitionCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Acquisitions', path: '/assets/acquisitions' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Acquisition' : isViewMode ? 'View Acquisition' : 'New Acquisition'}</h1>
          <p>{isViewMode ? 'View acquisition details' : isEditMode ? 'Update acquisition information' : 'Record a new asset acquisition'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/acquisitions')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Acquisition Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Asset Details</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Acquisition Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="acquisitionCode"
                  value={formData.acquisitionCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Asset Name <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="assetName"
                  value={formData.assetName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Asset Tag</label>
                <input
                  type="text"
                  name="assetTag"
                  value={formData.assetTag}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Serial Number</label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Asset Type</label>
                <select
                  name="assetType"
                  value={formData.assetType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Asset Type</option>
                  {assetTypes.map(type => (
                    <option key={type._id} value={type._id}>
                      {type.assetTypeName} ({type.assetTypeCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Vendor &amp; Location</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Vendor</label>
                <select
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Vendor</option>
                  {vendors.map(vendor => (
                    <option key={vendor._id} value={vendor._id}>
                      {vendor.vendorName} ({vendor.vendorCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Location</label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>
                      {loc.locationName} ({loc.locationCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Purchase Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Purchase Date</label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Purchase Cost</label>
                <input
                  type="number"
                  name="purchaseCost"
                  value={formData.purchaseCost}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {currencyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Warranty Expiry</label>
                <input
                  type="date"
                  name="warrantyExpiry"
                  value={formData.warrantyExpiry}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={fullWidthStyle}>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetAcquisitionForm
